import { generateNextSpotLabels } from "@gettactic/map-builder/src/components/map/helpers/generateNextSpotLabels";
import { useShapeMetrics } from "@gettactic/map-builder/src/components/map/helpers/shapeMetrics";
import type {
  Shape,
  SpotOrientation,
  SpotShape
} from "@gettactic/map-builder/src/types";
import { useHotkeys } from "react-hotkeys-hook";
import type { DuplicateOverride } from "../document/operations";
import { useEditorStore } from "../store/EditorStoreContext";

const DUPLICATE_MARGIN = 2;

type Direction = "right" | "left" | "bottom" | "top";

const DIRECTIONS: Record<string, Direction> = {
  ArrowRight: "right",
  ArrowLeft: "left",
  ArrowDown: "bottom",
  ArrowUp: "top"
};

const flip = (
  orientation: SpotOrientation,
  direction: Direction
): SpotOrientation => {
  if (direction === "right" || direction === "left") {
    if (orientation === "left") return "right";
    if (orientation === "right") return "left";
    return orientation;
  }
  if (orientation === "top") return "bottom";
  if (orientation === "bottom") return "top";
  return orientation;
};

/**
 * Cmd/Ctrl + arrow duplicates the selected desks next to themselves in
 * that direction, flipping orientation and continuing the label pattern,
 * as the Konva provider did. Sizes come from the rendered shapes so the
 * gap between rows is exact.
 */
export const useDuplicateShortcuts = (): void => {
  const store = useEditorStore();
  const metrics = useShapeMetrics();

  useHotkeys(
    [
      "meta+up",
      "meta+down",
      "meta+left",
      "meta+right",
      "ctrl+up",
      "ctrl+down",
      "ctrl+left",
      "ctrl+right"
    ],
    (event) => {
      const direction = DIRECTIONS[event.key];
      if (!direction || !metrics) return;
      const state = store.getState();
      const spots = state.selection
        .map((id) => state.shapes[id])
        .filter((shape): shape is SpotShape => shape?.type === "spot");
      if (spots.length === 0) return;
      event.preventDefault();

      const sizes = spots.map((spot) => metrics.getElementSize(spot.id));
      const minX = Math.min(...sizes.map((size) => size?.visualX ?? 0));
      const minY = Math.min(...sizes.map((size) => size?.visualY ?? 0));
      const maxX = Math.max(
        ...sizes.map((size) => (size?.visualX ?? 0) + (size?.visualWidth ?? 0))
      );
      const maxY = Math.max(
        ...sizes.map((size) => (size?.visualY ?? 0) + (size?.visualHeight ?? 0))
      );
      const groupWidth = maxX - minX;
      let groupHeight = maxY - minY;

      // A top↔bottom flip moves the drawing inside the shape box; correct
      // the offset so the visual gap stays exact (not for composites, whose
      // companions do not move on flip).
      const hasComposite = spots.some((spot) => spot.companions?.length);
      if (!hasComposite && (direction === "top" || direction === "bottom")) {
        const flips = spots.some(
          (spot) => spot.orientation === "top" || spot.orientation === "bottom"
        );
        if (flips) {
          const shapeMinY = Math.min(...spots.map((spot) => spot.y));
          const shapeMaxY = Math.max(
            ...spots.map((spot) => spot.y + spot.height)
          );
          const topPad = minY - shapeMinY;
          const bottomPad = shapeMaxY - maxY;
          groupHeight +=
            direction === "bottom" ? topPad - bottomPad : bottomPad - topPad;
        }
      }

      let label = state.meta.lastSpotLabel ?? [];
      const override: DuplicateOverride = spots.map((spot) => {
        const patch: Partial<Shape> & { id: string } = {
          id: spot.id,
          x: spot.x,
          y: spot.y,
          orientation: flip(spot.orientation, direction)
        } as Partial<SpotShape> & { id: string };
        if (direction === "right")
          patch.x = spot.x + groupWidth + DUPLICATE_MARGIN;
        if (direction === "left")
          patch.x = spot.x - groupWidth - DUPLICATE_MARGIN;
        if (direction === "bottom")
          patch.y = spot.y + groupHeight + DUPLICATE_MARGIN;
        if (direction === "top")
          patch.y = spot.y - groupHeight - DUPLICATE_MARGIN;
        if (direction === "right" || direction === "bottom") {
          label =
            generateNextSpotLabels(state.meta.spotLabelPattern, label) ?? label;
          (patch as Partial<SpotShape>).label = label.join("");
        }
        return patch;
      });

      state.duplicateShapes(
        spots.map((spot) => spot.id),
        { offset: { x: 0, y: 0 }, override }
      );
      if (direction === "right" || direction === "bottom") {
        store.getState().setMeta({ lastSpotLabel: label });
      }
    },
    { preventDefault: false, enableOnFormTags: false },
    [store, metrics]
  );
};
